import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import FolderSidebar from "./FolderSidebar";
import ContentGrid from "./ContentGrid";
import BreadcrumbNav from "./BreadcrumbNav";
import ScenarioDetailsDrawer from "./ScenarioDetailsDrawer";
import ContentGridItem from "./ContentGridItem";
import FolderDeleteConfirmDialog from "./FolderDeleteConfirmDialog";
import { wouldCreateCircularReference } from "./folderUtils";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      width: "90vw",
      maxWidth: 1200,
      height: "80vh",
      maxHeight: 800,
      borderRadius: 5,
      display: "flex",
      flexDirection: "column",
      margin: 10,
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    flexShrink: 0,
  },
  dialogTitleText: {
    fontSize: "1.125rem",
    fontWeight: 500,
    margin: 0,
    color: theme.palette.secondary.main,
  },
  closeButton: {
    color: theme.palette.grey[500],
  },
  dialogContent: {
    display: "flex",
    padding: 0,
    overflow: "hidden",
    position: "relative",
    flex: 1,
    backgroundColor: "#ffffff",
  },
  mainArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: "margin-right 0.3s ease",
  },
  mainAreaShifted: {
    marginRight: 470, // Width of details drawer
  },
  footer: {
    padding: theme.spacing(1.5, 2),
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  dragOverlay: {
    opacity: 0.9,
  },
}));

const ScenarioManagerModal = ({
  open,
  onClose,
  folders,
  scenarios,
  onScenarioMove,
  onFolderMove,
  onScenarioUpdate,
  onScenarioArchive,
  onScenarioRestore,
  onScenarioDelete,
  onScenarioOpen,
  onFolderCreate,
  onFolderDelete,
  onFolderRename,
  clientColor,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenario_manager",
  });
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = All
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  // Folder delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogAnchor, setDeleteDialogAnchor] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Reset selection when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedFolderId(null);
      setSelectedScenarioId(null);
    }
  }, [open]);

  // Build breadcrumb path
  const breadcrumbPath = useMemo(() => {
    if (
      selectedFolderId === null ||
      selectedFolderId === -1 ||
      selectedFolderId === "archive"
    ) {
      return [];
    }

    const findPath = (foldersArray, targetId, path = []) => {
      for (const folder of foldersArray) {
        if (folder.folder_id === targetId) {
          return [...path, folder];
        }
        if (folder.subfolders?.length > 0) {
          const found = findPath(folder.subfolders, targetId, [
            ...path,
            folder,
          ]);
          if (found) return found;
        }
      }
      return null;
    };

    return findPath(folders, selectedFolderId) || [];
  }, [folders, selectedFolderId]);

  // Calculate scenario counts for each folder based on actual scenarios
  const foldersWithCounts = useMemo(() => {
    // Build a map of folder_id -> count
    const countMap = new Map();

    // Count scenarios per folder (only non-archived scenarios)
    scenarios.forEach((scenario) => {
      if (!scenario.is_archived && scenario.folder_id) {
        countMap.set(
          scenario.folder_id,
          (countMap.get(scenario.folder_id) || 0) + 1,
        );
      }
    });

    // Recursively update folders with calculated counts (including nested scenarios)
    const addCountsToFolders = (foldersArray) => {
      return foldersArray.map((folder) => {
        const updatedSubfolders = folder.subfolders
          ? addCountsToFolders(folder.subfolders)
          : [];

        // Direct count for this folder
        const directCount = countMap.get(folder.folder_id) || 0;

        // Sum of all subfolder counts (recursive)
        const nestedCount = updatedSubfolders.reduce(
          (sum, subfolder) => sum + (subfolder.scenario_count || 0),
          0,
        );

        return {
          ...folder,
          scenario_count: directCount + nestedCount,
          subfolders: updatedSubfolders,
        };
      });
    };

    return addCountsToFolders(folders);
  }, [folders, scenarios]);

  // Get content for current folder
  const currentContent = useMemo(() => {
    let subfolders = [];
    let folderScenarios = [];

    if (selectedFolderId === null) {
      // All scenarios - show only scenarios, folders are in sidebar
      subfolders = [];
      folderScenarios = scenarios.filter((s) => !s.is_archived);
    } else if (selectedFolderId === -1) {
      // Ungrouped
      folderScenarios = scenarios.filter((s) => !s.folder_id && !s.is_archived);
    } else if (selectedFolderId === "archive") {
      // Archived
      folderScenarios = scenarios.filter((s) => s.is_archived);
    } else {
      // Specific folder
      const findFolder = (foldersArray, id) => {
        for (const f of foldersArray) {
          if (f.folder_id === id) return f;
          if (f.subfolders?.length > 0) {
            const found = findFolder(f.subfolders, id);
            if (found) return found;
          }
        }
        return null;
      };

      const folder = findFolder(foldersWithCounts, selectedFolderId);
      subfolders = folder?.subfolders || [];
      folderScenarios = scenarios.filter(
        (s) => s.folder_id === selectedFolderId && !s.is_archived,
      );
    }

    return { subfolders, scenarios: folderScenarios };
  }, [foldersWithCounts, scenarios, selectedFolderId]);

  // Counts for sidebar
  const ungroupedCount = scenarios.filter(
    (s) => !s.folder_id && !s.is_archived,
  ).length;
  const archivedCount = scenarios.filter((s) => s.is_archived).length;

  // Selected scenario object
  const selectedScenario = selectedScenarioId
    ? scenarios.find((s) => s.scenario_id === selectedScenarioId)
    : null;

  // Drag handlers
  const handleDragStart = (event) => {
    setActiveItem(event.active.data.current);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveItem(null);

    if (!over) return;

    const draggedItem = active.data.current;
    const targetData = over.data.current;

    // Scenario dropped on folder in sidebar
    if (draggedItem.scenario_id && targetData?.folder_id) {
      onScenarioMove(draggedItem.scenario_id, targetData.folder_id);
    }
    // Scenario dropped on ungrouped
    else if (draggedItem.scenario_id && targetData?.type === "ungrouped") {
      onScenarioMove(draggedItem.scenario_id, null);
    }
    // Scenario dropped on archive
    else if (draggedItem.scenario_id && targetData?.type === "archive") {
      onScenarioArchive(draggedItem.scenario_id);
    }
    // Folder dropped on folder - check for circular reference
    else if (
      draggedItem.folder_id &&
      targetData?.folder_id &&
      draggedItem.folder_id !== targetData.folder_id
    ) {
      // Prevent circular reference
      if (
        wouldCreateCircularReference(
          folders,
          draggedItem.folder_id,
          targetData.folder_id,
        )
      ) {
        toast.error(t("circular_reference_error"));
        return;
      }
      onFolderMove(draggedItem.folder_id, targetData.folder_id);
    }
  };

  // Folder delete handlers
  const handleFolderDeleteClick = (folder, anchorEl) => {
    setFolderToDelete(folder);
    setDeleteDialogAnchor(anchorEl);
    setDeleteDialogOpen(true);
  };

  const handleFolderDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFolderToDelete(null);
    setDeleteDialogAnchor(null);
  };

  const handleFolderDeleteConfirm = () => {
    if (!folderToDelete || !onFolderDelete) return;

    const deletedFolder = folderToDelete;

    // Close dialog first
    setDeleteDialogOpen(false);

    // Navigate away if viewing deleted folder
    if (selectedFolderId === deletedFolder.folder_id) {
      setSelectedFolderId(null);
    }

    // Call the delete handler (which handles optimistic update and toast)
    onFolderDelete(deletedFolder.folder_id);

    // Clean up state
    setFolderToDelete(null);
    setDeleteDialogAnchor(null);
  };

  const handleFolderClick = (folderId) => {
    setSelectedFolderId(folderId);
    setSelectedScenarioId(null); // Close drawer when navigating
  };

  const handleScenarioClick = (scenarioId) => {
    setSelectedScenarioId(scenarioId);
  };

  const handleDrawerClose = () => {
    setSelectedScenarioId(null);
  };

  // Close drawer when clicking outside (on main area background)
  const handleMainAreaClick = (e) => {
    // Only close if clicking directly on the main area or content grid background
    // Don't close if clicking on a scenario (that's handled by handleScenarioClick)
    if (selectedScenarioId && e.target === e.currentTarget) {
      setSelectedScenarioId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth={false}
    >
      <Box className={classes.dialogTitle}>
        <Typography className={classes.dialogTitleText}>
          {t("title")}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Left Sidebar */}
          <FolderSidebar
            folders={foldersWithCounts}
            selectedFolderId={selectedFolderId}
            onFolderSelect={handleFolderClick}
            onFolderDelete={
              onFolderDelete ? handleFolderDeleteClick : undefined
            }
            onFolderRename={onFolderRename}
            ungroupedCount={ungroupedCount}
            archivedCount={archivedCount}
            clientColor={clientColor}
          />

          {/* Main Content Area */}
          <Box
            className={`${classes.mainArea} ${selectedScenarioId ? classes.mainAreaShifted : ""}`}
            onClick={handleMainAreaClick}
          >
            {/* Breadcrumb */}
            <BreadcrumbNav
              path={breadcrumbPath}
              onNavigate={handleFolderClick}
              onCreateFolder={() => {
                // Sanitize: -1 (ungrouped) and "archive" are UI values, not valid folder IDs
                const parentId =
                  selectedFolderId === null ||
                  selectedFolderId === -1 ||
                  selectedFolderId === "archive"
                    ? null
                    : selectedFolderId;
                onFolderCreate(parentId);
              }}
            />

            {/* Icon Grid */}
            <ContentGrid
              folders={currentContent.subfolders}
              scenarios={currentContent.scenarios}
              selectedScenarioId={selectedScenarioId}
              onFolderClick={handleFolderClick}
              onScenarioClick={handleScenarioClick}
              onBackgroundClick={handleDrawerClose}
              clientColor={clientColor}
            />

            {/* Footer */}
            <Box className={classes.footer}>
              {t("items_count", {
                count:
                  currentContent.subfolders.length +
                  currentContent.scenarios.length,
              })}
            </Box>
          </Box>

          {/* Details Drawer */}
          <ScenarioDetailsDrawer
            open={!!selectedScenarioId}
            scenario={selectedScenario}
            folders={foldersWithCounts}
            onClose={handleDrawerClose}
            onFolderChange={(id, folderId) => onScenarioMove(id, folderId)}
            onVisibilityChange={(id, vis) =>
              onScenarioUpdate(id, { visibility: vis })
            }
            onArchive={onScenarioArchive}
            onRestore={onScenarioRestore}
            onDelete={onScenarioDelete}
            onOpen={onScenarioOpen}
            onRefresh={() => {}}
            clientColor={clientColor}
          />

          {/* Drag Overlay */}
          <DragOverlay>
            {activeItem && (
              <Box className={classes.dragOverlay}>
                <ContentGridItem
                  type={activeItem.folder_id ? "folder" : "scenario"}
                  data={activeItem}
                  isSelected={false}
                  onClick={() => {}}
                  clientColor={clientColor}
                />
              </Box>
            )}
          </DragOverlay>
        </DndContext>

        {/* Folder Delete Confirmation Dialog */}
        <FolderDeleteConfirmDialog
          open={deleteDialogOpen}
          anchorEl={deleteDialogAnchor}
          folderName={folderToDelete?.name || ""}
          onConfirm={handleFolderDeleteConfirm}
          onCancel={handleFolderDeleteCancel}
        />
      </DialogContent>
    </Dialog>
  );
};

ScenarioManagerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folders: PropTypes.array.isRequired,
  scenarios: PropTypes.array.isRequired,
  onScenarioMove: PropTypes.func.isRequired,
  onFolderMove: PropTypes.func.isRequired,
  onScenarioUpdate: PropTypes.func.isRequired,
  onScenarioArchive: PropTypes.func.isRequired,
  onScenarioRestore: PropTypes.func.isRequired,
  onScenarioDelete: PropTypes.func.isRequired,
  onScenarioOpen: PropTypes.func.isRequired,
  onFolderCreate: PropTypes.func.isRequired,
  onFolderDelete: PropTypes.func,
  onFolderRename: PropTypes.func,
  clientColor: PropTypes.string,
};

ScenarioManagerModal.defaultProps = {
  clientColor: "#1976d2",
};

export default ScenarioManagerModal;
